import { useMemo } from "react";
import { MenuPlacement } from "react-select";
import cn from "classnames";
import { Nullable } from "shared/lib/typeCheck.lib";
import { useTranslation } from "shared/localization/localization";
import { CarrierIcon } from "shared/ui/CarrierIcon";
import { ComponentsClassNames, CustomSelect } from "shared/ui/CustomSelect";
import { AllSeaCarriersIcon } from "shared/ui/Icons";
import { createSealineIconUrl } from "shared/utils/url.utils";
import styles from "./SealineSelect.module.scss";

import { useSealinesQuery } from "../api/hooks/useSealinesQuery";

type CarrierOptionType = {
  value: string;
  label: string;
  icon?: JSX.Element;
  isDropDownMenuRelative?: boolean;
};

type SealineSelectProps = {
  value: Nullable<string>;
  onChange: (value: CarrierOptionType) => void;
  className?: string;
  isDropDownMenuRelative?: boolean;
  menuPlacement?: MenuPlacement;
  componentsClassNames?: ComponentsClassNames;
  showAllCarriersOption?: boolean;
  placeholderDataTestId?: string;
  isDisabled?: boolean;
};

export const SealineSelect: RFC<SealineSelectProps> = ({
  value,
  onChange,
  className,
  isDropDownMenuRelative = true,
  componentsClassNames,
  menuPlacement = "auto",
  showAllCarriersOption = false,
  placeholderDataTestId,
  isDisabled = false,
}) => {
  const { t } = useTranslation();
  const { seaLineOptions } = useSealinesQuery();

  const carrierOptions: CarrierOptionType[] = useMemo(() => {
    const defaultOption: CarrierOptionType = {
      value: "",
      label: t("All carriers"),
      icon: <AllSeaCarriersIcon />,
    };
    return [
      ...(showAllCarriersOption ? [defaultOption] : []),
      ...seaLineOptions.map((sealine) => ({
        value: sealine.scacCode,
        label: sealine.name,
        icon: (
          <CarrierIcon
            src={createSealineIconUrl(sealine.iconScac)}
            label={sealine.name}
            code={sealine.iconScac}
            className={styles.scacImg}
          />
        ),
      })),
    ];
  }, [seaLineOptions.length]);

  const carrierValue = useMemo(() => {
    return carrierOptions.find((option) => option.value === value) ?? null;
  }, [value, carrierOptions]);

  return (
    <CustomSelect<CarrierOptionType, false>
      className={className}
      placeholder={
        <div className={styles.placeholder}>
          <AllSeaCarriersIcon />
          {t("All carriers")}
        </div>
      }
      placeholderDataTestId={placeholderDataTestId}
      isDisabled={isDisabled}
      options={carrierOptions}
      value={carrierValue}
      handleChange={onChange}
      isInputTypingEnabled
      menuPlacement={menuPlacement}
      isDropDownMenuRelative={isDropDownMenuRelative}
      selectedIndicator="checkmark"
      lazyLoadedMenuList
      componentsClassNames={{
        menuList: cn(styles.override, styles.shipmentSelectMenuList),
        ...componentsClassNames,
      }}
    />
  );
};
